import { Controller } from "@hotwired/stimulus"
import Swiper from 'swiper'
import { Navigation, Pagination, Thumbs, A11y } from 'swiper/modules'
// Swiper CSS comes from application.bootstrap.scss (swiper-bundle.min); importing
// it here makes esbuild emit its own application.css, clobbering the sass bundle.

export default class extends Controller {
  static targets = ["main", "thumbs"]
  static values = {
    galleryUrl: String,
    gallerySize: Number,
    productId: String,
  }

  connect() {
    this.galleryPromise = null
    this.galleryAbortController = null
    this.queueGalleryLoad = this.queueGalleryLoad.bind(this)
    this.handleNavigationClick = this.handleNavigationClick.bind(this)

    if (!this.hasMainTarget) return

    // Turbo snapshots retain dynamically appended slides but create a fresh
    // Stimulus controller on restoration. Derive the state from the DOM so a
    // restored, complete gallery is not fetched and appended a second time.
    this.galleryLoaded = this.galleryIsComplete()
    if (this.galleryLoaded) this.markGalleryLoaded()

    // Two slides are rendered with the card so the carousel and its controls
    // are usable immediately. Additional slides can arrive afterward.
    this.initSwiper()

    if (!this.galleryLoaded) {
      this.bindDeferredGalleryEvents()
    }
  }

  disconnect() {
    this.unbindDeferredGalleryEvents()
    this.galleryAbortController?.abort()

    if (this.mainSwiper) {
      this.mainSwiper.destroy()
      this.mainSwiper = null
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy()
      this.thumbsSwiper = null
    }
  }

  bindDeferredGalleryEvents() {
    // Desktop shoppers normally hover before clicking an arrow; keyboard and
    // touch users trigger the equivalent events. A navigation click starts a
    // load as a fallback, without delaying Swiper's already-available action.
    this.element.addEventListener("pointerenter", this.queueGalleryLoad)
    this.element.addEventListener("focusin", this.queueGalleryLoad)
    this.element.addEventListener("touchstart", this.queueGalleryLoad, { passive: true })
    this.mainTarget.addEventListener("click", this.handleNavigationClick)
  }

  unbindDeferredGalleryEvents() {
    this.element.removeEventListener("pointerenter", this.queueGalleryLoad)
    this.element.removeEventListener("focusin", this.queueGalleryLoad)
    this.element.removeEventListener("touchstart", this.queueGalleryLoad)
    if (this.hasMainTarget) {
      this.mainTarget.removeEventListener("click", this.handleNavigationClick)
    }
  }

  queueGalleryLoad() {
    void this.loadDeferredGallery()
  }

  handleNavigationClick(event) {
    if (this.galleryLoaded) return

    if (!(event.target instanceof Element)) return
    const button = event.target.closest(".swiper-button-next, .swiper-button-prev")
    if (!button || !this.mainTarget.contains(button)) return

    this.queueGalleryLoad()
  }

  galleryIsComplete() {
    if (!this.hasGalleryUrlValue) return true
    if (!this.hasGallerySizeValue || this.gallerySizeValue <= 0) return false

    return this.mainTarget.querySelectorAll(".swiper-slide").length >= this.gallerySizeValue
  }

  markGalleryLoaded() {
    this.galleryLoaded = true
    // Persist completion in Turbo's DOM snapshot. The slide-count check above
    // remains a fallback for snapshots made by older code that kept this URL.
    this.element.removeAttribute("data-swiper-collection-product-gallery-url-value")
  }

  loadDeferredGallery() {
    if (this.galleryLoaded) return Promise.resolve(true)
    if (this.galleryPromise) return this.galleryPromise

    this.galleryAbortController = new AbortController()
    this.galleryPromise = this.fetchDeferredGallery(this.galleryAbortController.signal)
    return this.galleryPromise
  }

  async fetchDeferredGallery(signal) {
    try {
      const response = await fetch(this.galleryUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal,
      })
      if (!response.ok) throw new Error(`Gallery request failed (${response.status})`)

      const payload = await response.json()
      if (!Array.isArray(payload.images)) throw new Error("Gallery response has no images array")
      if (!this.element.isConnected) return false

      this.appendDeferredImages(payload.images)
      this.markGalleryLoaded()
      this.unbindDeferredGalleryEvents()
      this.mainSwiper?.update()
      return true
    } catch (error) {
      if (error?.name !== "AbortError") console.warn("Unable to load product-card gallery", error)
      return false
    } finally {
      this.galleryPromise = null
      this.galleryAbortController = null
    }
  }

  appendDeferredImages(images) {
    const wrapper = this.mainTarget.querySelector(".swiper-wrapper")
    const currentLink = wrapper?.querySelector(`.variant-link-${this.productIdValue}`)
    if (!wrapper || !currentLink) return

    for (const imageData of images) {
      if (!imageData?.src) continue

      const slide = document.createElement("div")
      slide.className = "swiper-slide px-md-2 px-2"

      const link = document.createElement("a")
      link.className = `variant-link-${this.productIdValue}`
      link.setAttribute("href", currentLink.getAttribute("href") || "#")
      link.dataset.turboFrame = currentLink.dataset.turboFrame || "_top"
      for (const attribute of ["title", "aria-label"]) {
        if (currentLink.hasAttribute(attribute)) {
          link.setAttribute(attribute, currentLink.getAttribute(attribute))
        }
      }

      const image = document.createElement("img")
      image.src = imageData.src
      image.width = 400
      image.height = 400
      image.className = "img-fluid product-card-img"
      image.loading = "lazy"
      image.alt = imageData.alt || ""
      image.dataset.pid = this.productIdValue

      link.appendChild(image)
      slide.appendChild(link)
      wrapper.appendChild(slide)
    }
  }

  initSwiper() {
    if (this.mainSwiper) return

    // Initialize Thumbs Swiper first
    if (this.hasThumbsTarget) {
      this.thumbsSwiper = new Swiper(this.thumbsTarget, {
        modules: [Navigation, Thumbs],
        spaceBetween: 10,
        slidesPerView: 4,
        freeMode: true,
        watchSlidesProgress: true,
        breakpoints: {
          320: {
            slidesPerView: 3,
          },
          768: {
            slidesPerView: 4,
          }
        }
      })
    }

    // Initialize Main Swiper
    if (this.hasMainTarget) {
      const modules = []
      const config = {
        modules: modules,
        spaceBetween: 10,
        // Scope to mainTarget — a collection page renders one of these per
        // product card alongside the Recently Viewed / You May Also Like
        // sliders. Bare string selectors resolve against the document and
        // bind every match, and Swiper's uniqueNavElements only narrows them
        // while the buttons stay inside .swiper (see DESIGN.md §14, which
        // places them outside it).
        grabCursor: true
      }

      const nextEl = this.mainTarget.querySelector('.swiper-button-next')
      const prevEl = this.mainTarget.querySelector('.swiper-button-prev')
      if (nextEl && prevEl) {
        modules.push(Navigation)
        config.navigation = {
          nextEl,
          prevEl,
        }
      }

      const paginationEl = this.mainTarget.querySelector('.swiper-pagination')
      if (paginationEl) {
        modules.push(Pagination)
        config.pagination = {
          el: paginationEl,
          type: 'bullets',
          clickable: true
        }
      }

      if (this.thumbsSwiper) {
        modules.push(Thumbs)
        config.thumbs = {
          swiper: this.thumbsSwiper
        }
      }

      this.mainSwiper = new Swiper(this.mainTarget, config)
    }
  }
}
